import { meetingTypesRoutes, routes } from '@/routes/routes'
import type {
  MeetingReportFilterControl,
  MeetingReportFiltersSnapshot,
  MeetingSourceProductSlug,
  MeetingStatusSlug,
} from '@/components/meeting-reports/filters/analytics'
import type { MeetingReportRole } from '@/components/meeting-reports/filters/role'

/**
 * PostHog event taxonomy for chilical.
 *
 * `ChilicalEvent` is the registry — call sites reference the constant, never a bare string,
 * matching `SchedulerEvent` in chilical-scheduler and `ChiliChatEvent` in @chilipiper/tracking.
 *
 * `ChilicalEventPayloads` types the properties for each event. `useTrackFeature` uses both, so a
 * wrong event name _or_ a wrong payload is a compile error. Adding an event means adding it in both
 * places — the `Record<ChilicalEventName, unknown>` constraint on the payload map makes a missing
 * entry fail to compile rather than silently accept `any`.
 *
 * `getFeatureFromRoute` below maps routes onto the same feature set, for the `feature` super
 * property.
 *
 * Conventions:
 *
 * - Event names are `snake_case`, area-prefixed, past-tense.
 * - Property names are `snake_case`.
 * - Prefer stable ids over user-authored free text (high cardinality, and names can carry information
 *   the analytics project should not hold).
 * - `void` means the event carries no properties beyond the mandatory `feature`.
 */

/**
 * Top-level product area an event rolls up to. Every custom event carries a `feature` property, so
 * a single breakdown-by-`feature` ranks the whole app regardless of event name.
 *
 * `chat` and `email_composer` are cross-product surfaces reached from the nav and are role-gated —
 * they only ever appear on a `nav_item_clicked`, so treat them separately from native features when
 * ranking usage.
 */
export const ChilicalFeature = {
  Calendar: 'calendar',
  Availability: 'availability',
  MeetingTypes: 'meeting_types',
  MeetingReports: 'meeting_reports',
  Integrations: 'integrations',
  PersonalPage: 'personal_page',
  Chat: 'chat',
  EmailComposer: 'email_composer',
  Onboarding: 'onboarding',
} as const

export type ChilicalFeatureName = (typeof ChilicalFeature)[keyof typeof ChilicalFeature]

export const ChilicalEvent = {
  NavItemClicked: 'nav_item_clicked',

  CalendarEventOpened: 'calendar_event_opened',
  CalendarSlotSelected: 'calendar_slot_selected',
  CalendarViewChanged: 'calendar_view_changed',
  PrioritySchedulingToggled: 'priority_scheduling_toggled',
  MeetingCreated: 'meeting_created',
  MeetingUpdated: 'meeting_updated',
  MeetingCancelled: 'meeting_cancelled',

  AvailabilityModalOpened: 'availability_modal_opened',
  AvailabilityTabViewed: 'availability_tab_viewed',
  AvailabilityScheduleCreated: 'availability_schedule_created',
  AvailabilityScheduleDeleted: 'availability_schedule_deleted',
  AvailabilityScheduleDuplicated: 'availability_schedule_duplicated',

  MeetingTypeCreated: 'meeting_type_created',

  MeetingReportFiltersChanged: 'meeting_report_filters_changed',
  MeetingReportDetailOpened: 'meeting_report_detail_opened',
  MeetingReportMeetingActioned: 'meeting_report_meeting_actioned',
  MeetingReportExported: 'meeting_report_exported',

  IntegrationConnectClicked: 'integration_connect_clicked',
  IntegrationDisconnectClicked: 'integration_disconnect_clicked',

  OnboardingProfileCompleted: 'onboarding_profile_completed',
  OnboardingInviteAccepted: 'onboarding_invite_accepted',
  OnboardingInviteDeclined: 'onboarding_invite_declined',
  OnboardingWorkingHoursSet: 'onboarding_working_hours_set',
  OnboardingChromeExtensionInstallClicked: 'onboarding_chrome_extension_install_clicked',
  OnboardingFinished: 'onboarding_finished',
  OnboardingEmailStepCompleted: 'onboarding_email_step_completed',
} as const

export type ChilicalEventName = (typeof ChilicalEvent)[keyof typeof ChilicalEvent]

/** Calendar providers, as analytics slugs. Never send the UI display title. */
export type CalendarProviderSlug = 'google_calendar' | 'microsoft_outlook'

/** Video-conferencing providers, as analytics slugs. */
export type VideoProviderSlug =
  | 'zoom'
  | 'microsoft_teams'
  | 'webex'
  | 'ring_central'
  | 'go_to_meeting'

/** Mailbox (send-from-inbox) providers, as analytics slugs. */
export type MailboxProviderSlug = 'gmail' | 'outlook_mail'

/**
 * Which integrations UI emitted the event. Both generations render in production behind
 * `rolloutFlags.multiCalendarFeature`, so without this the two populations are indistinguishable in
 * analysis.
 */
export type IntegrationsUiVersion = 'legacy' | 'multi_calendar'

/**
 * Calendar integration events must say which slot they refer to, so "share of secondary-calendar
 * connects" is answerable. Video providers have no such distinction, so `calendar_type` is not part
 * of their payload at all.
 */
type CalendarIntegrationTarget = {
  provider: CalendarProviderSlug
  calendar_type: 'primary' | 'secondary'
}

type VideoIntegrationTarget = {
  provider: VideoProviderSlug
}

/** The rep's own mailbox (CEH-11688). Like video providers it has no `calendar_type`. */
type MailboxIntegrationTarget = {
  provider: MailboxProviderSlug
}

type IntegrationTarget = (
  | CalendarIntegrationTarget
  | VideoIntegrationTarget
  | MailboxIntegrationTarget
) & {
  ui_version: IntegrationsUiVersion
}

/**
 * Forces every name in `ChilicalEvent` to have a payload entry. Adding an event to the registry
 * without one is a compile error here, not a runtime surprise.
 */
type PayloadsFor<T extends Record<ChilicalEventName, unknown>> = T

export type ChilicalEventPayloads = PayloadsFor<{
  // navigation — the `feature` argument carries the destination
  nav_item_clicked: void

  // calendar
  calendar_event_opened: { event_type: string }
  calendar_slot_selected: { entry_mode: 'meeting' | 'task' }
  calendar_view_changed: {
    direction: 'prev' | 'next' | 'today'
    source: 'button' | 'keyboard'
  }
  priority_scheduling_toggled: { enabled: boolean }
  meeting_created: { meeting_type_id: string | undefined }
  meeting_updated: { meeting_id: string }
  meeting_cancelled: { meeting_id: string }

  // availability
  availability_modal_opened: { source: 'calendar' }
  availability_tab_viewed: { tab: 'custom' | 'default' }
  availability_schedule_created: { schedule_id: string }
  availability_schedule_deleted: { schedule_id: string; schedule_name: string | undefined }
  availability_schedule_duplicated: { schedule_id: string; schedule_name: string | undefined }

  // meeting types
  meeting_type_created: { meeting_type_id: string; meeting_type_name: string }

  // meeting reports — `role` is which side of the meeting the list is scoped to. The filters event
  // is a snapshot of the whole state, sent once per state the user settles on, so every control's
  // usage and the empty-result rate come from one event; the search text and the meeting type ids
  // never leave the browser.
  meeting_report_filters_changed: MeetingReportFiltersSnapshot & {
    changed: MeetingReportFilterControl
    result_count: number
  }
  meeting_report_detail_opened: {
    meeting_id: string
    role: MeetingReportRole
    status: MeetingStatusSlug
    source_product: MeetingSourceProductSlug | 'unknown'
    /** How far down the list the user was, in loaded rows. */
    rows_loaded: number
  }
  meeting_report_meeting_actioned: {
    action: 'cancel' | 'update' | 'reminder' | 'reassign'
    /** `unconfirmed`: the update was accepted but had not shown up when the poll gave up. */
    result: 'success' | 'error' | 'unconfirmed'
    role: MeetingReportRole
  }
  meeting_report_exported: {
    role: MeetingReportRole
    applied_filter_count: number
    result_count: number
    result: 'success' | 'error'
  }

  // integrations
  integration_connect_clicked: IntegrationTarget & {
    /**
     * Whether the user is recovering an existing connection whose credentials went bad
     * (`InTrouble`) rather than connecting for the first time. Required, not optional, so
     * "reconnect rate" has no null bucket.
     */
    reconnect: boolean
  }
  integration_disconnect_clicked: IntegrationTarget

  // onboarding
  onboarding_profile_completed: { job_title: string }
  onboarding_invite_accepted: void
  onboarding_invite_declined: void
  onboarding_working_hours_set: void
  onboarding_chrome_extension_install_clicked: void
  onboarding_finished: void
  onboarding_email_step_completed: { provider: MailboxProviderSlug; allowed: boolean }
}>

// Separate from NavLink.feature: that is where you navigate *to*, this is where you already are
// Whole-segment match, so /availability claims /availability/custom/:id but not -report
const routeFeatures: ReadonlyArray<readonly [string, ChilicalFeatureName]> = [
  [routes.onboarding.index, ChilicalFeature.Onboarding],
  [routes.home.index, ChilicalFeature.Calendar],
  [routes.home.calendar, ChilicalFeature.Calendar],
  [routes.inbox, ChilicalFeature.Chat],
  [routes.emailAgentReview, ChilicalFeature.EmailComposer],
  [meetingTypesRoutes.meetingTypes(), ChilicalFeature.MeetingTypes],
  [routes.meetingReports.index, ChilicalFeature.MeetingReports],
  [routes.availability.index, ChilicalFeature.Availability],
  [routes.personalPage, ChilicalFeature.PersonalPage],
  [routes.integrations, ChilicalFeature.Integrations],
]

/** Undefined for areas the taxonomy does not cover (/personal-settings, /gtmo) — never fall back. */
export const getFeatureFromRoute = (pathname: string): ChilicalFeatureName | undefined =>
  routeFeatures.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.[1]
